import { Eye, Lightbulb, MessageCircleQuestion, Quote } from 'lucide-react'
import { useState } from 'react'

import type { BookAiSection, BookAiThesis } from '@/api/services/book-ai'
import { Button } from '@/components/ui/button'

type Props = {
  thesis: BookAiThesis | null
  sections: BookAiSection[]
  onNavigate: (key: number) => void
}

/** 本の主張: 一文で・最も言いたいこと・主張を支える柱・持ち帰ること・批判的に読む点 */
export const ThesisTab = ({ thesis, sections, onNavigate }: Props) => {
  // 小説は主題に結末が含まれることがあるので、開くまで隠す
  const [revealed, setRevealed] = useState(false)

  if (!thesis) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        すべての章の解析が終わると、本全体の主張をまとめます
      </p>
    )
  }

  if (thesis.genre === 'fiction' && !revealed) {
    return (
      <div className="space-y-3 py-6 text-center">
        <p className="text-sm text-muted-foreground">物語の主題には結末に触れる内容が含まれる場合があります</p>
        <Button variant="outline" onClick={() => setRevealed(true)}>
          <Eye className="size-4" />
          表示する
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="rounded-xl bg-primary/10 p-4">
        <Quote className="mb-1 size-4 text-primary" />
        <p className="text-base font-semibold leading-relaxed wrap-break-word">{thesis.one_liner}</p>
      </div>

      <section className="space-y-1.5">
        <h3 className="text-xs font-semibold text-muted-foreground">
          {thesis.genre === 'fiction' ? '物語の主題' : '著者が最も伝えたいこと'}
        </h3>
        <p className="text-sm leading-relaxed wrap-break-word">{thesis.main_claim}</p>
      </section>

      {thesis.arguments.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-xs font-semibold text-muted-foreground">主張を支える柱</h3>
          <ol className="space-y-2">
            {thesis.arguments.map((argument, i) => (
              <li key={i} className="rounded-lg border p-3">
                <p className="text-sm font-medium wrap-break-word">
                  {i + 1}. {argument.point}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground wrap-break-word">{argument.support}</p>
                {argument.section_indexes.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {argument.section_indexes.map((sectionIndex) => {
                      const section = sections.find((s) => s.index === sectionIndex)
                      if (!section) return null
                      return (
                        <button
                          key={sectionIndex}
                          type="button"
                          className="max-w-full truncate rounded-full bg-muted px-2.5 py-1 text-xs text-primary"
                          onClick={() => onNavigate(section.start)}
                        >
                          {section.title}
                        </button>
                      )
                    })}
                  </div>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}

      {thesis.takeaways.length > 0 && (
        <section className="space-y-1.5">
          <h3 className="flex items-center gap-1 text-xs font-semibold text-muted-foreground">
            <Lightbulb className="size-3.5" />
            {thesis.genre === 'fiction' ? '読後に考えたいこと' : '持ち帰ること'}
          </h3>
          <ul className="list-disc space-y-1 pl-5 text-sm">
            {thesis.takeaways.map((takeaway, i) => (
              <li key={i} className="wrap-break-word">
                {takeaway}
              </li>
            ))}
          </ul>
        </section>
      )}

      {thesis.open_questions.length > 0 && (
        <section className="space-y-1.5">
          <h3 className="flex items-center gap-1 text-xs font-semibold text-muted-foreground">
            <MessageCircleQuestion className="size-3.5" />
            批判的に読むなら
          </h3>
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {thesis.open_questions.map((question, i) => (
              <li key={i} className="wrap-break-word">
                {question}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
